import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AssemblerService } from "./assembler.service";
import { MemoryAccessActor, MemoryService } from "./memory.service";
import { MenuItem, MessageService } from "primeng/api";
import { CPUService } from "./cpu.service";

import { CPURegisterIndex } from "./cpuregs";

import { Subscription, timer } from "rxjs";

import { IrqCtrlService } from "./irqctrl.service";
import { TimerService } from "./timer.service";
import { KeypadComponent } from "./keypad/keypad.component";
import { VisualDisplayComponent } from "./visual-display/visual-display.component";
import { TextualDisplayComponent } from "./textual-display/textual-display.component";
import { instructionSet } from "./instrset";
import { ByteData, CodeRamMapping, CPUSpeed, IConfiguration } from "./types";

import * as ace from "ace-builds";
import "../asm-sim";
// import 'ace-builds/src-noconflict/mode-assembly_x86';
import "ace-builds/src-noconflict/theme-dracula";
import "ace-builds/src-noconflict/ext-language_tools";

const THEME = "ace/theme/dracula";
const LANG = "ace/mode/asm-sim";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  providers: [MessageService],
})
export class AppComponent implements AfterViewInit {
  title = "asm-simulator";

  public ramContent: Array<ByteData>;
  public mapping: CodeRamMapping;
  public labels: Map<string, number>;
  public displayA = false;
  public displayB = false;
  public displayC = false;
  public displayD = false;
  public isRunning = false;
  public menuItems: MenuItem[];
  public speedItems: MenuItem[];
  public showCentralPane: boolean;
  /* Current configuration */
  public config: IConfiguration;
  @ViewChild("codeEditor", { static: true }) codeEditorElmRef: ElementRef;
  @ViewChild("fileInput", { static: true }) fileInput: ElementRef;
  @ViewChild("fileDownload", { static: true }) fileDownload: ElementRef;
  @ViewChild(KeypadComponent, { static: true })
  keypadComponent: KeypadComponent;
  @ViewChild(VisualDisplayComponent, { static: true })
  visualDisplayComponent: VisualDisplayComponent;
  @ViewChild(TextualDisplayComponent, { static: true })
  textualDisplayComponent: TextualDisplayComponent;
  private codeEditor: ace.Ace.Editor;
  private resetConfiguration: IConfiguration = {
    rightBarVisible: true,
    clearBytesAfterPop: false,
    followLineWhileRunning: true,
    easyMode: true,
    cpuSpeed: 0,
    ioRegistersPanel: {
      visible: true,
      size: 25,
    },
    memoryPanel: {
      visible: true,
      size: 25,
    },
    cpuRegistersPanel: {
      visible: true,
      size: 25,
    },
    ioDevicesPanel: {
      visible: true,
      size: 25,
    },
    codePanel: {
      visible: true,
      size: 50,
    },
    eventsLogPanel: {
      visible: true,
      size: 50,
    },
  };
  private localStorageName = "asm-simulator-ws";
  private localStorageNameCode = "asm-simulator-ws-code";
  private timerSubscription: Subscription;

  private breakPointLineNumbers = new Set();

  constructor(
    private assemblerService: AssemblerService,
    private memoryService: MemoryService,
    private messageService: MessageService,
    private cpuService: CPUService,
    private irqCtrlService: IrqCtrlService,
    private timerService: TimerService,
    private http: HttpClient,
    private renderer: Renderer2,
    private _ngZone: NgZone
  ) {
    this.memoryService.getUserMode = () => {
      return this.cpuService.SR.supervisor === 1
        ? MemoryAccessActor.CPU_SUPERVISOR
        : MemoryAccessActor.CPU_USER;
    };

    if (localStorage.getItem(this.localStorageName)) {
      this.config = JSON.parse(localStorage.getItem(this.localStorageName));
      instructionSet.allowEasyModeInstructions = this.config.easyMode;

      this.memoryService.clearBytesAfterPop = this.config.clearBytesAfterPop;
    } else {
      this.resetConfig();
    }

    if (window.innerWidth >= 1400) {
      this.showCentralPane = true;
      this.config.memoryPanel.visible = false;
    } else {
      this.showCentralPane = false;
      this.config.memoryPanel.visible = true;
    }

    const changeSpeed = (newSpeed: CPUSpeed) => (event) => {
      this.speedItems[this.config.cpuSpeed].icon = undefined;
      this.config.cpuSpeed = newSpeed;
      event.item.icon = "pi pi-check";

      if (this.isRunning === true) {
        this.stop();
        this.run();
      }

      this.saveConfig();
    };

    this.speedItems = [
      {
        label: "4 Hz",
        icon:
          this.config.cpuSpeed === CPUSpeed._4Hz ? "pi pi-check" : undefined,
        command: changeSpeed(CPUSpeed._4Hz),
      },
      {
        label: "16 Hz",
        icon:
          this.config.cpuSpeed === CPUSpeed._16Hz ? "pi pi-check" : undefined,
        command: changeSpeed(CPUSpeed._16Hz),
      },
      {
        label: "64 Hz",
        icon:
          this.config.cpuSpeed === CPUSpeed._64Hz ? "pi pi-check" : undefined,
        command: changeSpeed(CPUSpeed._64Hz),
      },
      {
        label: "256 Hz",
        icon:
          this.config.cpuSpeed === CPUSpeed._256Hz ? "pi pi-check" : undefined,
        command: changeSpeed(CPUSpeed._256Hz),
      },
      {
        label: "1 kHz",
        icon:
          this.config.cpuSpeed === CPUSpeed._1kHz ? "pi pi-check" : undefined,
        command: changeSpeed(CPUSpeed._1kHz),
      },
      {
        label: "4 kHz",
        icon:
          this.config.cpuSpeed === CPUSpeed._4kHz ? "pi pi-check" : undefined,
        command: changeSpeed(CPUSpeed._4kHz),
      },
    ];

    const getConfigMenuItems = () => {
      return [
        {
          label: "Easy Mode",
          icon: this.config.easyMode ? "pi pi-check" : undefined,
          command: () => {
            this.onEasyModeToggle();
            this.menuItems.find(({ label }) => label === "Config").items =
              getConfigMenuItems();
          },
        },
        {
          label: "Clear Byte After Pop",
          icon: this.config.clearBytesAfterPop ? "pi pi-check" : undefined,
          command: () => {
            this.onClearBytesAfterPopToggle();
            this.menuItems.find(({ label }) => label === "Config").items =
              getConfigMenuItems();
          },
        },
        {
          label: "Follow Execution Line",
          icon: this.config.followLineWhileRunning ? "pi pi-check" : undefined,
          command: () => {
            this.onFollowingLineWhileRunningToggle();
            this.menuItems.find(({ label }) => label === "Config").items =
              getConfigMenuItems();
          },
        },
      ];
    };

    this.menuItems = [
      {
        label: "File",
        items: [
          {
            label: "Upload",
            icon: "pi pi-upload",
            command: () => this.fileInput.nativeElement.click(),
          },
          {
            label: "Download",
            icon: "pi pi-download",
            command: () => {
              const blob = new Blob([this.editorContent], {
                type: "text/plain;charset=utf-8;",
              });
              const url = URL.createObjectURL(blob);
              this.renderer.setAttribute(
                this.fileDownload.nativeElement,
                "href",
                url
              );
              this.fileDownload.nativeElement.click();
            },
          },
          {
            label: "Samples",
            items: [
              {
                label: "Hello World",
                command: () => this.setCodeSample(1),
              },
              {
                label: "Draw Sprite",
                command: () => this.setCodeSample(2),
              },
              {
                label: "Basic Interrupt Handling",
                command: () => this.setCodeSample(3),
              },
              {
                label: "System Calls",
                command: () => this.setCodeSample(4),
              },
              {
                label: "Exceptions",
                command: () => this.setCodeSample(5),
              },
              {
                label: "Sched & Dispatch",
                command: () => this.setCodeSample(6),
              },
            ],
          },
        ],
      },
      {
        label: "Config",
        icon: "pi pi-wrench",
        items: getConfigMenuItems(),
      },
      {
        label: "Assemble",
        icon: "pi pi-arrow-right",
        command: () => this.assemble(),
      },
      {
        label: "Run",
        icon: "pi pi-play",
        styleClass: "menubar-button-run",
        command: () => {
          if (this.isRunning === true) {
            this.stop();
          } else {
            this.run();
          }
        },
      },
      {
        label: "Step",
        icon: "pi pi-forward",
        command: () => this.executeStep(),
      },
      {
        label: "Speed",
        icon: "pi pi-slider-h",
        items: this.speedItems,
      },
      // {
      //   label: 'Reset',
      //   icon: 'pi pi-power-off',
      //   command: () => this.reset()
      // },
    ];
  }

  get editorContent() {
    if (this.codeEditor) {
      return this.codeEditor.getValue();
    }
  }

  set editorContent(content: string) {
    if (this.codeEditor) {
      this.codeEditor.setValue(content);
    }
  }

  ngAfterViewInit() {
    ace.require("ace/ext/language_tools");
    const element = this.codeEditorElmRef.nativeElement;

    const editorOptions: Partial<ace.Ace.EditorOptions> & { [k: string]: any } =
      {
        highlightActiveLine: true,
        enableBasicAutocompletion: true,
        autoScrollEditorIntoView: true,
        copyWithEmptySelection: true,
        fontFamily: "Source Code Pro",
        liveAutoCompletion: true,
        showFoldWidgets: true,
        enableAutoIndent: true,
        showPrintMargin: false,
      };
    this.codeEditor = ace.edit(element, editorOptions);

    this.codeEditor.setOptions({ overscroll: "half" });
    this.codeEditor.setTheme(THEME);
    this.codeEditor.getSession().setMode(LANG);
    this.codeEditor.on("change", () => this.handleCodeChange());
    this.loadSavedCode();
    this.subscribeGutterClick();
  }

  subscribeGutterClick() {
    this.codeEditor.on("guttermousedown" as any, (e: any) => {
      const target = e.domEvent.target;

      if (target.className.indexOf("ace_gutter-cell") === -1) {
        return;
      }

      if (!this.codeEditor.isFocused()) {
        return;
      }

      if (e.clientX > 40 + target.getBoundingClientRect().left) {
        return;
      }

      const row = e.getDocumentPosition().row;
      const breakpoints = e.editor.session.getBreakpoints(row, 0);

      // If there's a breakpoint already defined, it should be removed, offering the toggle feature
      if (typeof breakpoints[row] === typeof undefined) {
        e.editor.session.setBreakpoint(row);
        this.breakPointLineNumbers.add(row);
      } else {
        e.editor.session.clearBreakpoint(row);
        this.breakPointLineNumbers.delete(row);
      }

      e.stop();
    });
  }

  onClearBytesAfterPopToggle() {
    this.config.clearBytesAfterPop = !this.config.clearBytesAfterPop;
    this.memoryService.clearBytesAfterPop = this.config.clearBytesAfterPop;
    this.saveConfig();
  }

  onFollowingLineWhileRunningToggle() {
    this.config.followLineWhileRunning = !this.config.followLineWhileRunning;
    this.saveConfig();
  }

  onEasyModeToggle() {
    this.config.easyMode = !this.config.easyMode;
    instructionSet.allowEasyModeInstructions = this.config.easyMode;
    this.saveConfig();
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    if (window.innerWidth >= 1400) {
      this.showCentralPane = true;
      this.config.memoryPanel.visible = false;
    } else {
      this.showCentralPane = false;
      this.config.memoryPanel.visible = true;
    }
    this.saveConfig();
  }

  public onFileChange(event: any) {
    const file = event.target.files[0];

    const reader = new FileReader();

    reader.onload = () => (this.editorContent = reader.result as string);
    reader.readAsText(file);
  }

  public onDragEnd(
    splitNumber: number,
    e: { gutterNum: number; sizes: Array<number> }
  ) {
    this._ngZone.run(() => {
      this.updatePanelSizes(splitNumber, e.sizes);
    });
  }

  onRightPanelToggle() {
    this.config.rightBarVisible = !this.config.rightBarVisible;
    this.saveConfig();
  }

  handleCodeChange() {
    localStorage.setItem(this.localStorageNameCode, this.editorContent);
  }

  public assemble() {
    this.reset();

    let result;

    try {
      result = this.assemblerService.go(this.editorContent);
    } catch (e) {
      if (e.line) {
        this.pushErrorMessage(`${e.line}: ${e.error}`);
      } else if (e.error) {
        this.pushErrorMessage(e.error);
      } else {
        this.pushErrorMessage(e.toString());
      }

      return;
    }

    this.ramContent = result.ramContent;
    this.mapping = result.mapping;
    this.labels = result.labels;

    this.memoryService.storeBytes(
      0,
      this.ramContent.length,
      this.ramContent.map((bd) => bd.value)
    );
  }

  public memoryCellClick(address: number) {
    if (this.mapping && this.mapping.has(address) && this.codeEditor) {
      const { lineNumber: line } = this.mapping.get(address);
      this.scrollToLine(line);
    }
  }

  public executeStep() {
    try {
      this.cpuService.step();
      this.scrollToIPLine();
    } catch (e) {
      this.pushErrorMessage(e.message);
    }
  }

  private scrollToIPLine() {
    if (this.mapping && this.mapping.has(this.cpuService.IP.silentValue)) {
      const { lineNumber: line } = this.mapping.get(
        this.cpuService.IP.silentValue
      );
      this.scrollToLine(line);
    }
  }

  public run() {
    this.isRunning = true;

    this.menuItems[3].icon = "pi pi-pause";
    this.menuItems[3].label = "Pause";

    let steps = 1;
    let period;

    switch (this.config.cpuSpeed) {
      case CPUSpeed._4Hz: {
        period = 250;
        break;
      }
      case CPUSpeed._16Hz: {
        period = 125;
        break;
      }
      case CPUSpeed._64Hz: {
        period = 32;
        steps = 2;
        break;
      }
      case CPUSpeed._256Hz: {
        period = 32;
        steps = 8;
        break;
      }
      case CPUSpeed._1kHz: {
        period = 32;
        steps = 32;
        break;
      }
      case CPUSpeed._4kHz: {
        period = 32;
        steps = 128;
        break;
      }
    }

    this.timerSubscription = timer(1, period).subscribe(() => {
      try {
        for (let i = 0; i < steps; i++) {
          this.cpuService.step();

          const executionIsSlowEnough = steps === 1;
          if (this.config.followLineWhileRunning && executionIsSlowEnough) {
            this.scrollToIPLine();
          }

          if (this.cpuService.isHalted) {
            this.stop();
          }

          if (
            this.mapping &&
            this.mapping.has(this.cpuService.IP.silentValue)
          ) {
            const { lineNumber } = this.mapping.get(
              this.cpuService.IP.silentValue
            );

            if (this.breakPointLineNumbers.has(lineNumber)) {
              this.stop();

              this.scrollToLine(lineNumber);
              break;
            }
          }
        }
      } catch (e) {
        this.pushErrorMessage(e.toString());
        this.stop();
      }
    });
  }

  public stop() {
    this.isRunning = false;
    this.updateRunButtonAndForceRender();

    if (this.timerSubscription && this.timerSubscription.closed === false) {
      this.timerSubscription.unsubscribe();
    }
  }

  private updateRunButtonAndForceRender() {
    const newMenuItemsRef = [...this.menuItems];
    newMenuItemsRef[3].icon = "pi pi-play";
    newMenuItemsRef[3].label = "Run";

    this.menuItems = newMenuItemsRef;
  }

  public reset() {
    if (this.isRunning === true) {
      this.stop();
    }

    this.mapping = undefined;
    this.cpuService.reset();
    this.memoryService.reset();
    this.irqCtrlService.reset();
    this.timerService.reset();

    this.keypadComponent.reset();
    this.visualDisplayComponent.reset();
    this.textualDisplayComponent.reset();
    this.messageService.clear();

    this.ramContent = [];
  }

  public setCodeSample(sampleNum: number) {
    this.reset();

    this.http
      .get(`assets/samples/sample${sampleNum}.asm`, { responseType: "text" })
      .subscribe((text: string) => {
        this.editorContent = text;
        this.handleCodeChange();
      });
  }

  public toggleDisplayRegister(registerIndex: CPURegisterIndex) {
    switch (registerIndex) {
      case CPURegisterIndex.A:
        this.displayA = !this.displayA;
        break;
      case CPURegisterIndex.B:
        this.displayB = !this.displayB;
        break;
      case CPURegisterIndex.C:
        this.displayC = !this.displayC;
        break;
      case CPURegisterIndex.D:
        this.displayD = !this.displayD;
        break;
    }
  }

  private loadSavedCode() {
    if (localStorage.getItem(this.localStorageNameCode)) {
      this.editorContent = localStorage.getItem(this.localStorageNameCode);
    }
  }

  private pushErrorMessage(detail: string) {
    this.messageService.clear();
    this.messageService.add({ severity: "error", detail: detail });
  }

  private resetConfig() {
    this.config = JSON.parse(JSON.stringify(this.resetConfiguration));
    localStorage.removeItem(this.localStorageName);
  }

  private saveConfig() {
    localStorage.setItem(this.localStorageName, JSON.stringify(this.config));
  }

  private updatePanelSizes(splitNumber: number, sizes: Array<number>) {
    if (splitNumber === 0) {
      // We are on the left pane, and there is only one gutter
      this.config.codePanel.size = sizes[0];
      this.config.eventsLogPanel.size = sizes[1];
    } else {
      // We are on the right pane
      this.config.ioDevicesPanel.size = sizes[0];
      this.config.cpuRegistersPanel.size = sizes[1];
      if (
        this.config.memoryPanel.visible === true &&
        this.config.ioRegistersPanel.visible === true
      ) {
        this.config.memoryPanel.size = sizes[2];
        this.config.ioRegistersPanel.size = sizes[3];
      } else if (this.config.memoryPanel.visible === true) {
        this.config.memoryPanel.size = sizes[2];
      } else if (this.config.ioRegistersPanel.visible === true) {
        this.config.ioRegistersPanel.size = sizes[2];
      }
    }
    this.saveConfig();
  }

  private scrollToLine(line: number) {
    this.codeEditor.gotoLine(line + 1, 0, true);
    this.codeEditor.scrollToLine(line + 1, true, true, () => {});
  }
}
